// MyPage.jsx
import React, { useState, useCallback, useEffect } from 'react';
import './MyPage_original.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faSignOutAlt,
  faUserEdit,
  faHeadphones,
  faBullhorn,
  faTimes,  // 닫기 아이콘 추가
  faCamera,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logoutUser, setGuestLogin, updateUserProfile, updateProfilePicture, setLoading } from '../../store/slices/authSlice';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import axios from 'axios';
import { Storage } from '@capacitor/storage';
import LoadingSpinner from '../../Components/LoadingSpinner/LoadingSpinner';

const MyPage = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());

  const { user, isAuthenticated, isGuest, isLoading, token } = useSelector(
    (state) => state.auth
  );

  // Remove localProfilePic state and use user.profile_pic directly
  const profilePicUrl = user?.profile_pic;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 파일 타입 검사
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    // 파일 크기 검사 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB를 초과할 수 없습니다.');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      if (!token) {
        alert('로그인이 필요합니다.');
        navigate('/login');
        return;
      }

      console.log('Uploading profile picture...');
      const response = await axios.post(
        'https://fastapi-backend-657266059621.asia-northeast1.run.app/api/users/profile-picture',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.profile_pic) {
        console.log('Server response:', response.data);
        
        // Update Redux store
        await dispatch(updateProfilePicture(response.data.profile_pic));
        
        // Update timestamp to force image reload
        setImageTimestamp(Date.now());
        setImageError(false);
        
        alert('프로필 사진이 업데이트되었습니다.');
      }
    } catch (error) {
      console.error('Error details:', error);
      if (error.response?.status === 401) {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        dispatch(logoutUser());
        navigate('/login');
      } else {
        alert('프로필 사진 업로드에 실패했습니다.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Add useEffect to monitor user changes
  useEffect(() => {
    console.log('User state changed:', user);
  }, [user]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated && !isGuest) {
    return null;
  }

  const handleLogout = () => {
    // Show loading spinner
    dispatch(setLoading(true));

    // 1) sign out from Firebase
    signOut(auth).catch((err) => console.error('Firebase signOut error:', err));

    // 2) then clear Redux state and navigate with delay
    setTimeout(() => {
      dispatch(logoutUser())
        .then(() => {
          // Add additional delay before navigation
          setTimeout(() => {
            navigate('/login');
          }, 700);
        })
        .catch((error) => {
          console.error('Logout failed:', error);
          dispatch(setLoading(false));
        });
    }, 1000);
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const handleEditInfoClick = () => {
    navigate('/edit-info');
  };

  const handleNotificationSettingsClick = () => {
    navigate('/notification-settings');
  };

  const handleCustomerServiceClick = () => {
    navigate('/customer-service');
  };

  const handleAnnouncementsClick = () => {
    navigate('/announcements');
  };
  
  // NEW: admin-only route to create announcements
  const handleCreateAnnouncementClick = () => {
    navigate('/admin-announcements');
  };

  return (
    <div className="my-page">
      {/* [New] 닫기 버튼 */}
      {onClose && (
        <button className="close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}

      <div className="profile-section">
        <div className="profile-picture-container">
          {console.log('Rendering profile picture:', { profilePicUrl })}
          {profilePicUrl ? (
            <img 
              key={`${profilePicUrl}-${imageTimestamp}`}
              src={`${profilePicUrl}?t=${imageTimestamp}`}
              alt="Profile" 
              className="profile-picture"
              onError={handleImageError}
            />
          ) : (
            <FontAwesomeIcon icon={faUserCircle} className="profile-icon" />
          )}
          {!isGuest && (
            <label className="profile-picture-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                disabled={isUploading}
                style={{ display: 'none' }}
              />
              <FontAwesomeIcon icon={faCamera} className="camera-icon" />
            </label>
          )}
        </div>
        <h2>{user?.name || '게스트 사용자'}</h2>
        <p>{user?.email || 'guest@example.com'}</p>
      </div>

      <div className="settings-section">
        <h3>Settings</h3>
        <ul>
          <li onClick={handleEditInfoClick}>
            <FontAwesomeIcon icon={faUserEdit} style={{ marginRight: '8px' }} />
            회원정보 수정
          </li>
          {/*}
          <li onClick={handleNotificationSettingsClick}>
            <FontAwesomeIcon icon={faBell} style={{ marginRight: '8px' }} />
            알림 설정
          </li> */}
          <li onClick={handleCustomerServiceClick}>
            <FontAwesomeIcon icon={faHeadphones} style={{ marginRight: '8px' }} />
            고객센터
          </li>
          <li onClick={handleAnnouncementsClick}>
            <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: '8px' }} />
            공지사항
          </li>

          {/* Only show if user.id === 23 (our "admin") */}
          {user?.id === 23 && (
            <li onClick={handleCreateAnnouncementClick}>
              <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: '8px' }} />
              공지사항 등록 (Admin)
            </li>
          )}
        </ul>
      </div>

      {!isGuest ? (
        <button className="logout-button" onClick={handleLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
          Logout
        </button>
      ) : (
        <button className="logout-button" onClick={handleLoginRedirect}>
          <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
          Login
        </button>
      )}
    </div>
  );
};

export default MyPage;
